import type { ReactNode } from 'react'
import type { MascotConfig } from './mascot-catalog'
import type { MascotAppearance } from './mascot-appearance'
import { mixMascotColor } from './mascot-colors'
import { MascotAccessory, MascotDirt } from './MascotDetails'
import { MascotHeadwear } from './MascotDecorations'
import { SheepFace } from './SheepFace'
import styles from './DonorMascot.module.css'

/** The leaf and musical stem are drawn as vectors, like the supplied Agroplay reference. */
export function AgroplayEmblem() {
  return (
    <g fill="currentColor" data-agroplay-emblem>
      <path d="M0 13Q-13 13-10 3Q-1 1 0 13M2 7Q13 5 10-3Q1-3 2 7M-1 13Q-2-1 0-13L8-8 7-4 2-8 3 12Z" />
      <path
        d="M-10 16Q1 12 5 2M2 8 11 17"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <ellipse cx="-3" cy="11" rx="4" ry="2.5" transform="rotate(-30 -3 11)" />
    </g>
  )
}

export function SheepHat({ color = '#df907a' }: { color?: string }) {
  const shade = mixMascotColor(color, '#65332f', 0.42)
  return (
    <g data-sheep-hat data-headwear="ranch_hat" strokeLinejoin="round">
      <path
        d="M57 59 89 15Q98 3 115 14Q122 19 135 15Q153 8 164 27L181 65Z"
        fill={color}
        stroke={shade}
        strokeWidth="1.3"
      />
      <path d="M68 49Q119 30 169 49L180 66Q120 51 60 65Z" fill="#239c4c" />
      <path d="M69 48Q118 31 167 48" fill="none" stroke="#5bc668" strokeWidth="2.2" />
      <g transform="translate(123 48) scale(.48)" color="#fff9e7">
        <AgroplayEmblem />
      </g>
      <path
        d="M60 63Q17 65 15 33Q16 17 34 19Q50 20 47 34Q39 48 64 49L175 64Q198 70 194 48Q190 31 206 30Q227 30 227 48Q225 84 181 92Z"
        fill={shade}
      />
      <path
        d="M15 33Q11 66 59 69Q127 64 179 85Q215 94 227 48Q231 83 192 105L165 96 70 91Q32 84 15 33Z"
        fill={color}
        stroke={shade}
        strokeWidth="1.4"
      />
      <path
        d="M21 48Q33 69 67 65Q123 61 180 82Q210 91 222 62"
        fill="none"
        stroke={mixMascotColor(color, '#fff2cf', 0.28)}
        strokeWidth="2"
      />
    </g>
  )
}

export function SheepBandana({ color = '#20a64a' }: { color?: string }) {
  return (
    <g data-accessory="agroplay_bandana" className={styles.charm}>
      <path
        d="M82 158Q120 172 158 158L149 181 120 202 91 182Z"
        fill={color}
        stroke={mixMascotColor(color, '#184d31', 0.35)}
        strokeWidth="1.5"
      />
      <path
        d="M87 162Q120 175 153 162L147 173Q120 185 94 174Z"
        fill={mixMascotColor(color, '#08572e', 0.25)}
      />
      <path d="m94 181 26 18 26-18" fill="none" stroke="#b7e69e" strokeWidth="1.2" />
      <g transform="translate(121 183) scale(.52)" color="#fff9e9">
        <AgroplayEmblem />
      </g>
      <path d="m153 163 14 3-5 12-12-9" fill={color} />
    </g>
  )
}

export function SheepHead({
  config,
  appearance: a,
  paint,
}: {
  config: MascotConfig
  appearance: MascotAppearance
  paint: (name: string) => string
}) {
  return (
    <g className={styles.headPose} data-sheep-head>
      <g className={styles.headMotion}>
        {config.headwear === 'ranch_hat' && (
          <g transform="translate(0 -6)">
            <SheepHat color={config.headwearColor} />
          </g>
        )}
        <g className={styles.leftEarPose}>
          <g className={styles.leftEar} data-sheep-ear="left">
            <path
              d="M66 89Q38 87 19 122Q12 135 24 137Q54 139 75 109Z"
              fill={paint('fur')}
              stroke="#dfc8b4"
            />
            <path d="M64 96Q45 99 24 130Q43 131 66 111Z" fill="#eeb1bc" />
            <path
              d="M27 130q18-7 30-23"
              fill="none"
              stroke="#f8d0d2"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </g>
        </g>
        <g className={styles.rightEarPose}>
          <g className={styles.rightEar} data-sheep-ear="right">
            <path
              d="M174 91Q202 92 221 130Q226 141 216 142Q188 141 167 111Z"
              fill={paint('fur')}
              stroke="#dfc8b4"
            />
            <path d="M179 99Q198 106 215 135Q196 131 176 112Z" fill="#eeb1bc" />
            <path
              d="M211 133q-17-12-24-25"
              fill="none"
              stroke="#f8d0d2"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </g>
        </g>
        <path
          d="M51 105Q43 92 55 82Q51 65 69 63Q73 49 89 55Q101 43 116 54Q133 45 145 56Q164 51 171 67Q190 66 190 85Q203 96 190 111Q198 126 186 135Q188 152 171 154Q165 170 149 166Q135 180 120 170Q104 180 91 168Q73 173 68 157Q50 156 51 140Q38 127 51 115Z"
          fill={paint('fur')}
          stroke="#e6d4bc"
          strokeWidth="1"
        />
        <path
          d="M61 91Q64 65 119 66Q173 67 181 99L180 124Q176 157 124 162Q75 162 61 134Q55 118 61 91Z"
          fill="#f5c9c0"
          stroke="#e5a89e"
          strokeWidth="1.2"
        />
        <ellipse
          cx="119"
          cy="121"
          rx="59"
          ry="38"
          fill="#e4e6d7"
          opacity={a.sickness * 0.23}
        />
        <path
          d="M61 96Q45 102 44 89Q39 79 51 73Q46 61 60 59Q61 45 77 49Q84 35 102 48Q117 40 131 51Q149 42 159 57Q179 51 181 70Q198 71 191 85Q203 97 190 103Q178 106 174 92Q157 94 151 79Q135 91 119 78Q102 88 91 73Q78 84 66 78Q66 91 61 96Z"
          fill={paint('cream')}
          stroke="#eedfcd"
          strokeWidth="1"
          data-sheep-wool="fringe"
        />
        <path
          d="M69 67q9-9 17-2m23-5q10-6 18 1m24 5q9-3 14 5"
          fill="none"
          stroke="#fffef7"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <SheepFace appearance={a} eyeColor={config.eyeColor ?? '#81b64d'} />
        <MascotDirt amount={a.dirt} face />
        {config.headwear !== 'ranch_hat' && <MascotHeadwear config={config} />}
      </g>
    </g>
  )
}

const WOOL_BODY =
  'M85 149Q77 140 68 151Q57 151 61 166Q51 174 60 184Q50 197 62 206Q59 219 74 220Q79 234 92 227Q104 239 116 230Q129 239 140 229Q155 236 164 223Q180 224 179 209Q191 201 182 188Q191 175 180 168Q183 153 168 151Q159 139 148 149Z'

export function SheepBody({
  config,
  appearance: a,
  paint,
  head,
}: {
  config: MascotConfig
  appearance: MascotAppearance
  paint: (name: string) => string
  head: ReactNode
}) {
  const standing = config.pose === 'standing'
  const accessory =
    config.accessory === 'agroplay_bandana' ? (
      <SheepBandana color={config.accessoryColor} />
    ) : (
      <MascotAccessory
        accessory={config.accessory}
        accessoryColor={config.accessoryColor}
      />
    )
  if (a.sleeping)
    return (
      <g data-sheep-body="sleeping">
        <g transform="translate(26 56) scale(.95 .76)">
          <path d={WOOL_BODY} fill={paint('fur')} stroke="#e6d4bc" />
        </g>
        <path d="M174 211q22-17 28-3 7 14-12 20" fill={paint('cream')} stroke="#e6d4bc" />
        <ellipse cx="115" cy="228" rx="21" ry="7" fill="#eab6a7" />
        <path d="M98 227q17 9 32 0v5q-16 7-31-1Z" fill="#a6695a" />
        <g transform="translate(12 112) scale(.68)">
          <g transform="rotate(-65 120 158)">{accessory}</g>
          {head}
        </g>
        <g transform="translate(29 54) scale(.8)">
          <MascotDirt amount={a.dirt} />
        </g>
      </g>
    )
  return (
    <g data-sheep-body={config.pose}>
      <g className={styles.tail}>
        <path
          d="M167 199q27-19 28-1 8 12-5 17-10 4-20-4"
          fill={paint('fur')}
          stroke="#e6d4bc"
        />
      </g>
      <g fill="#dca18f" stroke="#bb8374" strokeWidth=".8">
        <path d="m78 207-5 27q6 6 14 1l7-25m52 0 7 25q8 4 14-2l-5-26" />
        <path d="m73 229 15 1-2 7q-9 5-13-1Zm80 1 13-2 2 8q-6 6-14 1Z" fill="#92594f" />
      </g>
      <path d={WOOL_BODY} fill={paint('fur')} stroke="#e6d4bc" strokeWidth="1.2" />
      <path
        d="M70 185q-7 10 4 17m4 12q8 9 17 3m48 2q10 7 17-4m10-15q8-6 1-14"
        fill="none"
        stroke="#e8d7c0"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <g
        className={styles.frontLegs}
        transform={standing ? undefined : 'translate(0 -3)'}
      >
        {[97, 140].map((x, i) => (
          <g
            key={x}
            transform={`rotate(${standing ? (i ? -3 : 3) : i ? -10 : 10} ${x} 217)`}
          >
            <path
              d={`M${x - 9} 211q9-6 18 0v26q-8 10-18 0Z`}
              fill="#efb7a8"
              stroke="#d99b8d"
            />
            <path d={`M${x - 9} 232q9 5 18 0v8q-8 7-18 0Z`} fill="#b37565" />
            <path d={`M${x} 235v7`} stroke="#8e584e" strokeWidth="1.2" />
            <path
              d={`M${x - 12} 208q-2-8 7-7 6-8 12 0 9-1 6 8 1 8-7 8-6 6-11-1-8 3-7-7Z`}
              fill={paint('cream')}
            />
          </g>
        ))}
      </g>
      <MascotDirt amount={a.dirt} />
      {accessory}
      <g transform={standing ? 'translate(0 -4)' : undefined}>{head}</g>
    </g>
  )
}
